import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@seen/ui'
import {
  IconChevronDown,
  IconChevronUp,
  IconSelector,
} from '@tabler/icons-react'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type TableState,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  type JSX,
  type MouseEvent,
  type ReactNode,
  useMemo,
  useRef,
} from 'react'
import { ContextMenuDeleteItem } from '#src/components/core/context-menu/ContextMenuDeleteItem.tsx'
import { DataTablePagination } from '#src/components/core/data-table/TablePagination.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import type { DeleteConfirmationProps } from '#src/hooks/useDeleteConfirmationStore.ts'

// Above this many rows the body virtualizes so tables with thousands of rows
// (practitioners, organizations) don't mount every row at once. Smaller tables
// render every row eagerly, unchanged.
const DEFAULT_VIRTUALIZE_THRESHOLD = 100
const ESTIMATED_ROW_HEIGHT = 52
const VIRTUALIZED_MAX_HEIGHT = 'max-h-[70vh]'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onRowClick?: (row: TData, event: MouseEvent<HTMLTableRowElement>) => void
  // Extra HTML attributes to spread onto a row's <tr>, e.g. for RUM
  // instrumentation. Stays domain-agnostic here; callers decide what (if
  // anything) to attach.
  getRowAttributes?: (row: TData) => Record<string, string> | undefined
  deleteRowProps?: (
    row: TData
  ) => DeleteConfirmationProps & { disabled?: boolean }
  contextMenuContent?: (row: TData) => ReactNode
  enableRowSelection?: boolean
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  enableSorting?: boolean
  onSortingChange?: OnChangeFn<SortingState>
  state?: Partial<TableState>
  getRowId?: (row: TData) => string
  onPaginationChange?: OnChangeFn<PaginationState>
  // Row count above which the body virtualizes. Ignored when paginated.
  virtualizeThreshold?: number
  // When true and there are no rows yet, show a loading row instead of the
  // "No results." empty state so an in-flight fetch doesn't read as "empty".
  isLoading?: boolean
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  getRowAttributes,
  deleteRowProps,
  contextMenuContent,
  enableRowSelection,
  onRowSelectionChange,
  enableSorting,
  onSortingChange,
  state,
  getRowId,
  onPaginationChange,
  virtualizeThreshold = DEFAULT_VIRTUALIZE_THRESHOLD,
  isLoading = false,
}: DataTableProps<TData, TValue>): JSX.Element {
  const pagingEnabled = !!state?.pagination
  const sortingEnabled = enableSorting && !!onSortingChange

  /* A new sort reorders the whole list, so whatever was on the current page
   * isn't there any more — jump back to the first page to show it. TanStack
   * does this itself via `autoResetPageIndex`, which we turn off below
   * because it also fires on every refetch. */
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    onSortingChange?.(updater)
    if (pagingEnabled) {
      onPaginationChange?.((previous) => ({ ...previous, pageIndex: 0 }))
    }
  }

  /* Any column with a `size` opts the table into `table-layout: fixed`,
   * with widths emitted via `<colgroup>`. Columns without an explicit
   * `size` get `width: auto`, sharing whatever space the sized columns
   * leave behind. Cells holding `whitespace-nowrap` content should add
   * `truncate` themselves; default wrapping content takes care of itself. */
  const hasSizedColumns = useMemo(
    () => columns.some((c) => c.size !== undefined),
    [columns]
  )

  const table = useReactTable({
    data,
    columns,
    // ColumnSizing feature contributes `size: 150` into the resolved
    // columnDef for every column, which shadows the "no `size` = auto"
    // contract we read off `column.columnDef.size` in the colgroup below.
    // Overriding with `undefined` here lets a truly-undefined user value
    // survive the spread merge in TanStack's `createColumn`.
    defaultColumn: { size: undefined },
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection,
    onRowSelectionChange,
    enableSorting: sortingEnabled,
    getSortedRowModel: sortingEnabled ? getSortedRowModel() : undefined,
    onSortingChange: sortingEnabled ? handleSortingChange : undefined,
    state,
    initialState: state,
    getRowId,
    getPaginationRowModel: pagingEnabled ? getPaginationRowModel() : undefined,
    onPaginationChange: pagingEnabled ? onPaginationChange : undefined,
    /* Left unset, this defaults on and resets the page index whenever the
     * `data` array identity changes — which every refetch does, since React
     * Query's structural sharing can't dedupe model class instances. That
     * dragged callers back to page 1 on any background refresh. The resets
     * that are actually wanted are explicit: sorting above, and search /
     * filter / tab changes in the callers. */
    autoResetPageIndex: false,
  })

  const rows = table.getRowModel().rows

  // Virtualize only large, non-paginated tables. Paginated callers already cap
  // the DOM to one page, and small tables are cheap to render in full.
  const virtualized = !pagingEnabled && rows.length > virtualizeThreshold
  const scrollRef = useRef<HTMLDivElement>(null)
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 8,
    enabled: virtualized,
  })

  const renderRow = (row: Row<TData>): JSX.Element => {
    const deleteProps = deleteRowProps?.(row.original)
    const customContent = contextMenuContent?.(row.original)
    const hasContextMenu = !!deleteProps || !!customContent
    return (
      <ContextMenu key={row.id}>
        <ContextMenuTrigger asChild>
          <TableRow
            data-selected={row.getIsSelected() && 'true'}
            className={cn(onRowClick && 'cursor-pointer')}
            onClick={
              onRowClick ? (e) => onRowClick(row.original, e) : undefined
            }
            {...getRowAttributes?.(row.original)}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        </ContextMenuTrigger>
        {hasContextMenu && (
          <ContextMenuContent>
            {customContent}
            {deleteProps && (
              <ContextMenuDeleteItem
                disabled={deleteProps?.disabled}
                deleteConfirmationProps={deleteProps}
              />
            )}
          </ContextMenuContent>
        )}
      </ContextMenu>
    )
  }

  const emptyRow = (
    <TableRow>
      <TableCell colSpan={columns.length} className="h-24 text-center">
        {isLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : (
          'No results.'
        )}
      </TableCell>
    </TableRow>
  )

  const virtualItems = rowVirtualizer.getVirtualItems()
  // Pad the tbody with a leading/trailing spacer row so the scroll height and
  // the fixed-layout colgroup widths are preserved while only the visible
  // window of rows is mounted.
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0
      ? rowVirtualizer.getTotalSize() -
        virtualItems[virtualItems.length - 1].end
      : 0

  return (
    <div className="rounded-md border">
      <div
        ref={scrollRef}
        className={cn(virtualized && `overflow-auto ${VIRTUALIZED_MAX_HEIGHT}`)}
      >
        <Table className={cn(hasSizedColumns && 'table-fixed')}>
          {hasSizedColumns && (
            <colgroup>
              {table.getVisibleLeafColumns().map((column) => (
                <col
                  key={column.id}
                  style={{
                    width:
                      column.columnDef.size === undefined
                        ? 'auto'
                        : `${column.columnDef.size}px`,
                  }}
                />
              ))}
            </colgroup>
          )}
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDirection = header.column.getIsSorted()
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(canSort && 'cursor-pointer select-none')}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {canSort && (
                            <span className="ml-1">
                              {sortDirection === 'asc' ? (
                                <IconChevronUp className="size-4" />
                              ) : sortDirection === 'desc' ? (
                                <IconChevronDown className="size-4" />
                              ) : (
                                <IconSelector className="size-4 text-muted-foreground" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {rows.length === 0 && emptyRow}
            {rows.length > 0 &&
              !virtualized &&
              rows.map((row) => renderRow(row))}
            {virtualized && rows.length > 0 && (
              <>
                {paddingTop > 0 && (
                  <tr aria-hidden style={{ height: paddingTop }} />
                )}
                {virtualItems.map((virtualItem) =>
                  renderRow(rows[virtualItem.index])
                )}
                {paddingBottom > 0 && (
                  <tr aria-hidden style={{ height: paddingBottom }} />
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>
      {pagingEnabled && <DataTablePagination table={table} />}
    </div>
  )
}
